<template>
    <div class="adv-shooting-wrap" :style="{ '--team-color': side.color }">
        <!-- ---------- Controls ---------- -->
        <div class="shoot-controls">
            <div class="shoot-toggle shoot-team-switch">
                <button
                    v-for="team in teams"
                    :key="`switch-${team.key}`"
                    type="button"
                    class="shoot-toggle__btn"
                    :class="{ 'shoot-toggle__btn--active': selectedTeam === team.key }"
                    @click="selectedTeam = team.key"
                >
                    <img v-if="team.logo" :src="team.logo" alt="">
                    <span>{{ team.name }}</span>
                </button>
            </div>
            <div class="shoot-controls__row">
                <div class="shoot-toggle">
                    <button
                        type="button"
                        class="shoot-toggle__btn"
                        :class="{ 'shoot-toggle__btn--active': allPeriodsSelected }"
                        @click="selectPeriod(null)"
                    >
                        Όλος ο αγώνας
                    </button>
                    <button
                        v-for="period in availablePeriods"
                        :key="`period-${period}`"
                        type="button"
                        class="shoot-toggle__btn"
                        :class="{ 'shoot-toggle__btn--active': !allPeriodsSelected && selectedPeriods.includes(period) }"
                        @click="selectPeriod(period)"
                    >
                        {{ periodLabel(period) }}
                    </button>
                </div>
                <div class="shoot-toggle">
                    <button type="button" class="shoot-toggle__btn" :class="{ 'shoot-toggle__btn--active': viewMode === 'dots' }" @click="viewMode = 'dots'">
                        Σημεία
                    </button>
                    <button type="button" class="shoot-toggle__btn" :class="{ 'shoot-toggle__btn--active': viewMode === 'zones' }" @click="viewMode = 'zones'">
                        Ζώνες
                    </button>
                </div>
            </div>
        </div>

        <div class="shoot-body">
            <!-- ---------- Court ---------- -->
            <div class="shoot-court-col">
                <shot-court :shots="viewMode === 'dots' ? displayedShots : teamShots" :mode="viewMode" />

                <div v-if="viewMode === 'dots'" class="court-legend">
                    <button
                        class="legend-toggle"
                        :class="{ inactive: !shotOutcomeFilters.missed }"
                        type="button"
                        @click="shotOutcomeFilters.missed = !shotOutcomeFilters.missed"
                    >
                        <i class="dot" :style="{ borderColor: side.color, backgroundColor: '#fff' }" />
                        Άστοχο
                    </button>
                    <button
                        class="legend-toggle"
                        :class="{ inactive: !shotOutcomeFilters.made }"
                        type="button"
                        @click="shotOutcomeFilters.made = !shotOutcomeFilters.made"
                    >
                        <i class="dot" :style="{ borderColor: side.color, backgroundColor: side.color }" />
                        Εύστοχο
                    </button>
                </div>
                <div v-else class="court-legend court-legend--zones">
                    <span><i class="zone-swatch zone-swatch--hot" /> Πολύ καλό</span>
                    <span><i class="zone-swatch zone-swatch--ok" /> Μέτριο</span>
                    <span><i class="zone-swatch zone-swatch--cold" /> Χαμηλό</span>
                    <small>Ποσοστό εύστοχων σουτ ανά ζώνη (χωρίς βολές)</small>
                </div>
            </div>

            <!-- ---------- Percentages + players ---------- -->
            <div class="shoot-side">
                <div class="shoot-rings">
                    <div v-for="ring in rings" :key="ring.key" class="shoot-ring">
                        <span class="shoot-ring__label">{{ ring.label }}</span>
                        <div class="shoot-ring__circle">
                            <doughnut-chart :chart-data="ring.data" :type="ring.label" :interactive="false" />
                            <span class="shoot-ring__pct">{{ ring.pct }}</span>
                        </div>
                        <span class="shoot-ring__sub">{{ ring.made }}/{{ ring.attempts }}</span>
                    </div>
                </div>

                <div class="players-panel">
                    <div class="players-panel__head">
                        <span>Παίκτες</span>
                        <div class="players-panel__actions">
                            <button type="button" :class="{ active: allPlayersSelected }" @click="setAllPlayers(true)">Όλοι</button>
                            <button type="button" :class="{ active: noPlayerSelected }" @click="setAllPlayers(false)">Κανένας</button>
                        </div>
                    </div>
                    <div class="players-panel__list">
                        <button
                            v-for="player in sortedPlayers"
                            :key="`${selectedTeam}-${player.key}`"
                            type="button"
                            class="shoot-player"
                            :class="{ 'shoot-player--off': !isPlayerSelected(player.key) }"
                            @click="togglePlayer(player.key)"
                        >
                            <span class="shoot-player__photo"><img :src="player.imageSrc" alt="" @error="handlePlayerImageError"></span>
                            <span class="shoot-player__name">{{ player.label }}</span>
                            <span class="shoot-player__stats">
                                <b>{{ player.made }}/{{ player.attempts }}</b>
                                <small>{{ player.attempts ? player.pct : '—' }}</small>
                            </span>
                        </button>
                        <div v-if="!sortedPlayers.length" class="players-panel__empty">Δεν υπάρχουν σουτ ακόμα</div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import DoughnutChart from '@/components/Doughnut.vue'
    import { BACKGROUNDS } from '@/utils/colors'
    import ShotCourt from '@/components/ShotCourt'
    import {
        buildPlayerList, buildShots, getTeamColors, getShotCategory, periodLabel, summarizeShots, toPercentLabel
    } from '@/utils/shotChart'

    const ASSETS_URL = 'https://storage.googleapis.com'

    export default {
        name: 'AdvancedShootingChart',
        components: {
            DoughnutChart,
            ShotCourt
        },
        props: {
            match: {
                type: Object,
                default: () => ({})
            }
        },
        data() {
            return {
                selectedTeam: 'team1',
                // player key -> shown, per team
                selectedPlayers: {
                    team1: {},
                    team2: {}
                },
                shotOutcomeFilters: {
                    made: true,
                    missed: true
                },
                selectedPeriods: [1, 2, 3, 4],
                // 'dots': every shot, 'zones': made/attempts per court zone
                viewMode: 'dots',
                defaultPlayerImg: require('@/assets/default_player.png')
            }
        },
        computed: {
            teamColors() {
                // the dots are drawn on the parquet
                return getTeamColors(this.match?.team1, this.match?.team2, BACKGROUNDS.court, 1.6)
            },
            teams() {
                return ['team1', 'team2'].map((key) => {
                    const team = this.match?.[key] || {}
                    return {
                        key,
                        name: team.name || (key === 'team1' ? 'Γηπεδούχοι' : 'Φιλοξενούμενοι'),
                        logo: team.logo ? `${ASSETS_URL}/${team.logo}` : null,
                        color: this.teamColors[key],
                        players: buildPlayerList(team.players, this.defaultPlayerImg),
                        shots: buildShots(team.players, key, this.teamColors[key])
                    }
                })
            },
            side() {
                return this.teams.find(team => team.key === this.selectedTeam) || this.teams[0]
            },
            availablePeriods() {
                const periods = new Set()
                this.teams.forEach(team => team.shots.forEach((shot) => {
                    if (Number.isFinite(shot.period) && shot.period > 0) periods.add(shot.period)
                }))
                const sorted = Array.from(periods).sort((a, b) => a - b)
                return sorted.length ? sorted : [1, 2, 3, 4]
            },
            allPeriodsSelected() {
                return this.availablePeriods.every(period => this.selectedPeriods.includes(period))
            },
            // shots of the selected team and periods, whoever took them
            periodShots() {
                return this.side.shots.filter(shot => !shot.period || this.selectedPeriods.includes(shot.period))
            },
            // ... only of the selected players: the base of every percentage and of the zones
            teamShots() {
                return this.periodShots.filter(shot => this.isPlayerSelected(shot.playerKey))
            },
            // ... and of the made / missed toggles: the dots on the court
            displayedShots() {
                return this.teamShots.filter(shot => this.shotOutcomeFilters[shot.made ? 'made' : 'missed'])
            },
            rings() {
                const summary = summarizeShots(this.teamShots)
                return [
                    { key: 'fg', label: 'Σουτ', ...summary.fg },
                    { key: 'three', label: 'Τρίποντα', ...summary.three },
                    { key: 'two', label: 'Δίποντα', ...summary.two }
                ].map(ring => ({
                    ...ring,
                    pct: toPercentLabel(ring.made, ring.attempts),
                    data: {
                        datasets: [{
                            data: ring.attempts ? [ring.made, ring.attempts - ring.made] : [0, 1],
                            backgroundColor: [this.side.color, '#ececf0'],
                            borderWidth: 0
                        }]
                    }
                }))
            },
            // field goals per player in the selected periods, best scorers first
            sortedPlayers() {
                const stats = {}
                this.periodShots.forEach((shot) => {
                    if (getShotCategory(shot) === 'ft') return
                    const entry = stats[shot.playerKey] || (stats[shot.playerKey] = { made: 0, attempts: 0, points: 0 })
                    entry.attempts += 1
                    if (shot.made) {
                        entry.made += 1
                        entry.points += Number(shot.points) || 0
                    }
                })
                return this.side.players
                    .map((player) => {
                        const entry = stats[player.key] || { made: 0, attempts: 0, points: 0 }
                        return { ...player, ...entry, pct: toPercentLabel(entry.made, entry.attempts) }
                    })
                    .sort((a, b) => (b.points - a.points) || (b.attempts - a.attempts) || (a.number - b.number))
            },
            allPlayersSelected() {
                return this.side.players.length > 0 && this.side.players.every(player => this.isPlayerSelected(player.key))
            },
            noPlayerSelected() {
                return this.side.players.every(player => !this.isPlayerSelected(player.key))
            }
        },
        watch: {
            availablePeriods: {
                immediate: true,
                handler(periods) {
                    const current = this.selectedPeriods.filter(period => periods.includes(period))
                    this.selectedPeriods = current.length ? current : [...periods]
                }
            }
        },
        methods: {
            periodLabel,
            selectPeriod(period) {
                // a second click on the only selected period goes back to the whole match
                if (period === null || (this.selectedPeriods.length === 1 && this.selectedPeriods[0] === period)) {
                    this.selectedPeriods = [...this.availablePeriods]
                    return
                }
                this.selectedPeriods = [period]
            },
            // players are shown unless switched off, so new players of a live match appear by themselves
            isPlayerSelected(playerKey) {
                return this.selectedPlayers[this.selectedTeam][playerKey] !== false
            },
            togglePlayer(playerKey) {
                this.$set(this.selectedPlayers[this.selectedTeam], playerKey, !this.isPlayerSelected(playerKey))
            },
            setAllPlayers(selected) {
                const next = {}
                this.side.players.forEach((player) => { next[player.key] = selected })
                this.selectedPlayers = { ...this.selectedPlayers, [this.selectedTeam]: next }
            },
            handlePlayerImageError(event) {
                event.target.src = this.defaultPlayerImg
            }
        }
    }
</script>

<style scoped>
.adv-shooting-wrap {
    width: 100%;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    overflow: hidden;
    font-family: Ubuntu, Arial, sans-serif;
}

/* ---------- Controls ---------- */
.shoot-controls {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 14px 12px 12px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.07);
}

.shoot-controls__row {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 10px;
    max-width: 100%;
}

.shoot-toggle {
    display: inline-flex;
    gap: 2px;
    max-width: 100%;
    padding: 3px;
    border-radius: 999px;
    background: #0d0d0f;
    overflow-x: auto;
    scrollbar-width: none;
}

.shoot-toggle::-webkit-scrollbar {
    display: none;
}

.shoot-toggle__btn {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    border-radius: 999px;
    color: rgba(255, 255, 255, 0.75);
    font-size: 0.8rem;
    font-weight: 700;
    white-space: nowrap;
    transition: background 0.2s ease, color 0.2s ease;
}

.shoot-toggle__btn:hover {
    color: #fff;
}

.shoot-toggle__btn--active {
    background: linear-gradient(90deg, #ef4444, #ff7133, orange);
    color: #fff;
}

.shoot-team-switch .shoot-toggle__btn {
    min-width: 0;
    padding: 5px 16px 5px 6px;
    font-size: 0.9rem;
}

.shoot-team-switch img {
    width: 26px;
    height: 26px;
    padding: 1px;
    border-radius: 50%;
    background: #fff;
    object-fit: contain;
}

/* ---------- Body ---------- */
.shoot-body {
    display: grid;
    grid-template-columns: minmax(0, 560px) minmax(260px, 1fr);
    gap: 20px;
    padding: 16px 18px 12px;
}

.shoot-court-col {
    min-width: 0;
}

.shoot-side {
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-width: 0;
}

/* ---------- Rings ---------- */
.shoot-rings {
    display: flex;
    justify-content: space-around;
    gap: 10px;
    padding-bottom: 12px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.07);
}

.shoot-ring {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
}

.shoot-ring__label {
    color: rgba(0, 0, 0, 0.5);
    font-size: 0.7rem;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}

.shoot-ring__circle {
    position: relative;
    width: 84px;
    height: 84px;
}

.shoot-ring__circle > div {
    position: absolute !important;
    top: 0;
    left: 0;
    width: 84px !important;
    height: 84px !important;
}

.shoot-ring__pct {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: #111;
    font-size: 17px;
    font-weight: 800;
    pointer-events: none;
}

.shoot-ring__sub {
    color: rgba(0, 0, 0, 0.5);
    font-size: 12px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
}

/* ---------- Players ---------- */
.players-panel {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;

    /* takes the height the court leaves, instead of making the row taller */
    height: 0;
    min-height: 220px;
}

.players-panel__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 6px;
    color: rgba(0, 0, 0, 0.5);
    font-size: 0.75rem;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}

.players-panel__actions {
    display: flex;
    gap: 4px;
}

.players-panel__actions button {
    padding: 4px 10px;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 999px;
    color: rgba(0, 0, 0, 0.6);
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0;
    text-transform: none;
}

.players-panel__actions button.active {
    border-color: transparent;
    background: #0d0d0f;
    color: #fff;
}

.players-panel__list {
    display: flex;
    flex: 1 1 0;
    flex-direction: column;
    gap: 2px;
    min-height: 0;
    overflow-y: auto;
}

.players-panel__empty {
    padding: 16px 0;
    color: rgba(0, 0, 0, 0.45);
    font-size: 0.85rem;
    text-align: center;
}

.shoot-player {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 5px 8px;
    border-radius: 10px;
    text-align: left;
    transition: background 0.15s ease, opacity 0.15s ease;
}

.shoot-player:hover {
    background: #f4f4f6;
}

.shoot-player--off {
    opacity: 0.38;
}

.shoot-player__photo {
    flex: 0 0 auto;
    width: 38px;
    height: 38px;
    border-radius: 50%;
    background: #f1f1f3;
    box-shadow: inset 0 0 0 2px var(--team-color);
    overflow: hidden;
}

.shoot-player__photo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: top;
}

.shoot-player__name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    color: #2b2b2e;
    font-size: 0.88rem;
    font-weight: 700;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.shoot-player__stats {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    line-height: 1.1;
    font-variant-numeric: tabular-nums;
}

.shoot-player__stats b {
    color: #111;
    font-size: 0.88rem;
}

.shoot-player__stats small {
    color: rgba(0, 0, 0, 0.5);
    font-size: 0.72rem;
    font-weight: 700;
}

/* ---------- Legend ---------- */
.court-legend {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 8px 16px;
    padding: 10px 0 2px;
    color: #2b2b2e;
    font-size: 13px;
    font-weight: 700;
}

.court-legend span,
.legend-toggle {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.shoot-team-switch .shoot-toggle__btn span {
    overflow: hidden;
    text-overflow: ellipsis;
}

.legend-toggle.inactive {
    opacity: 0.45;
    text-decoration: line-through;
    text-decoration-thickness: 2px;
}

.court-legend small {
    flex-basis: 100%;
    color: rgba(0, 0, 0, 0.45);
    font-size: 11px;
    font-weight: 600;
    text-align: center;
}

.dot {
    width: 14px;
    height: 14px;
    border: 3px solid;
    border-radius: 50%;
}

.zone-swatch {
    width: 12px;
    height: 12px;
    border-radius: 4px;
}

.zone-swatch--hot {
    background: #16a34a;
}

.zone-swatch--ok {
    background: #f59e0b;
}

.zone-swatch--cold {
    background: #dc2626;
}

/* ---------- Phone: rings, court, players one under the other ---------- */
@media only screen and (max-width: 900px) {
    .shoot-controls {
        padding: 12px 10px 10px;
        border-bottom: 0;
    }

    .shoot-team-switch {
        width: 100%;
    }

    .shoot-team-switch .shoot-toggle__btn {
        flex: 1 1 0;
        justify-content: center;
    }

    /* the period and view toggles (the team switch keeps its own padding) */
    .shoot-controls__row .shoot-toggle__btn {
        padding: 6px 11px;
    }

    .shoot-body {
        display: flex;
        flex-direction: column;
        gap: 12px;
        padding: 0 10px 10px;
    }

    .shoot-side {
        display: contents;
    }

    .shoot-rings {
        order: -1;
        padding: 4px 0 10px;
    }

    .players-panel {
        flex: 0 0 auto;
        height: auto;
        min-height: 0;
        padding-top: 10px;
        border-top: 1px solid rgba(0, 0, 0, 0.07);
    }

    .players-panel__list {
        flex: 0 0 auto;
        overflow: visible;
    }
}
</style>
